import {postAdminAdjustment} from "@/lib/adminAdjustment";
import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { ChevronDown, ChevronRight, CheckCircle, AlertCircle, Plus } from "lucide-react";

function pence(p: number) {
  return `£${(p / 100).toFixed(2)}`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

/* One line of the ledger: a posted charge against a shop. Not necessarily an
   order — an external driver request or a cancelled-order fee is a charge too. */
type Charge = {
  transactionId: number;
  kind: string;
  label: string;
  orderId: number | null;
  createdAt: string;
  amountPence: number;
  customerName: string | null;
  fulfilmentType: string | null;
  totalOrderPence: number | null;
  destinationPostcode?: string | null;
};

type StoreGroup = {
  storeId: number;
  storeName: string;
  chargedPence: number;
  creditedPence: number;
  confirmedPaymentsPence?: number;
  otherCreditsPence?: number;
  pendingPence?: number;
  payablePence?: number;
  /* Confirmed debt. Unpaid checkout holds are separately reported, never collected. */
  outstandingPence: number;
  charges: Charge[];
};

type LedgerData = {
  stores: StoreGroup[];
  grandTotalOwedPence: number;
};

type OrderStore = { id: number; name: string };

export default function AdminCashFees() {
  useAuth(); // ensure admin-only page is within auth context
  const { toast } = useToast();
  const token = localStorage.getItem("ayrshire_token");
  const [data, setData] = useState<LedgerData | null>(null);
  const [loading, setLoading] = useState(false);
  const [settling, setSettling] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Manual charge form state
  const [showManualCharge, setShowManualCharge] = useState(false);
  const [stores, setStores] = useState<OrderStore[]>([]);
  const [chargeStoreId, setChargeStoreId] = useState("");
  const [chargeAmount, setChargeAmount] = useState("");
  const [chargeNote, setChargeNote] = useState("");
  const [chargeType, setChargeType] = useState<"charge" | "credit" | "bank_payment">("charge");
  const [paymentDate, setPaymentDate] = useState(() => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(new Date()));
  const [bankReference, setBankReference] = useState("");
  const [charging, setCharging] = useState(false);

  useEffect(() => {
    if (!showManualCharge || stores.length > 0) return;
    fetch("/api/admin/order-stores", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d: { items: OrderStore[] }) => setStores(d.items ?? []))
      .catch(() => {});
  }, [showManualCharge, stores.length, token]);

  async function load() {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/cash-fee-ledger", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load");
      setData(await res.json() as LedgerData);
    } catch {
      toast({ title: "Error", description: "Could not load cash fee ledger", variant: "destructive", action: <ToastAction altText="Reload page" onClick={() => window.location.reload()}>Reload</ToastAction> });
    } finally {
      setLoading(false);
    }
  }

  async function settleStore(storeGroup: StoreGroup) {
    if (!confirm(
      `Write off the ${pence(storeGroup.outstandingPence)} owed by "${storeGroup.storeName}"?\n\n` +
      `This permanently forgives the debt and records no payment received. Left alone, it is taken automatically from the shop's next card order.`
    )) return;

    setSettling(storeGroup.storeId);
    try {
      const res = await fetch("/api/admin/ledger-settle", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        // storeId only — the server recomputes the amount so it cannot over-credit.
        body: JSON.stringify({ storeId: storeGroup.storeId, adjustmentKey: (()=>{const key=`store-write-off:${storeGroup.storeId}`;let reference=sessionStorage.getItem(key);if(!reference){reference=crypto.randomUUID();sessionStorage.setItem(key,reference);}return reference;})() }),
      });
      if (!res.ok) throw new Error("Failed");
      const d = await res.json() as { writtenOffPence: number };
      sessionStorage.removeItem(`store-write-off:${storeGroup.storeId}`);
      toast({ title: "Written off", description: `${pence(d.writtenOffPence)} cleared for ${storeGroup.storeName}` });
      await load();
    } catch {
      toast({ title: "Error", description: "Could not write off balance", variant: "destructive" });
    } finally {
      setSettling(null);
    }
  }

  function toggleExpand(storeId: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(storeId) ? next.delete(storeId) : next.add(storeId);
      return next;
    });
  }

  async function submitManualCharge(e: React.FormEvent) {
    e.preventDefault();
    const storeId = Number(chargeStoreId);
    if (charging) return;
    const parts = /^(\d+)(?:\.(\d{1,2}))?$/.exec(chargeAmount.trim());
    const amountPence = parts ? Number(parts[1]) * 100 + Number((parts[2] ?? "").padEnd(2, "0")) : 0;
    const isBankPayment = chargeType === "bank_payment";
    if (!storeId || !Number.isSafeInteger(amountPence) || amountPence <= 0 || amountPence > 2147483647 || (isBankPayment ? !bankReference.trim() || !paymentDate : !chargeNote.trim())) return;
    const storeName = stores.find((s) => s.id === storeId)?.name ?? `Store #${storeId}`;
    const isCredit = chargeType === "credit";
    const actionLabel = isBankPayment ? "bank transfer payment received" : isCredit ? "credit" : "charge";
    const detail = isBankPayment
      ? `Payment date: ${paymentDate}\nBank reference: ${bankReference.trim()}\n\nConfirm the money has arrived in the bank. This reduces their debt and records a receipt in the Hub cashbook.`
      : `Note: ${chargeNote.trim()}\n\nThis will appear in their cash fee ledger.`;
    if (!confirm(`Record ${actionLabel} of ${pence(amountPence)} for ${storeName}?\n\n${detail}`)) return;
    setCharging(true);
    try {
      const res=await postAdminAdjustment(isBankPayment ? '/api/admin/store-bank-payment' : '/api/admin/cash-fee-manual-charge',isBankPayment ? {storeId,amountPence,paidOn:paymentDate,reference:bankReference.trim(),note:chargeNote.trim()} : {storeId,amountPence,note:chargeNote.trim(),type:chargeType},{'Content-Type':'application/json',Authorization:`Bearer ${token}`});
      if (!res.ok) { const error = await res.json().catch(() => ({})) as {error?: string}; throw new Error(error.error ?? "Could not save entry"); }
      toast({ title: isBankPayment ? "Bank payment received" : isCredit ? "Credit added" : "Charge added", description: isBankPayment ? `${pence(amountPence)} received from ${storeName}. Recorded in their statement and the Hub cashbook.` : `${pence(amountPence)} ${isCredit ? "credit" : "charge"} added to ${storeName}'s ledger` });
      setChargeStoreId(""); setChargeAmount(""); setChargeNote(""); setChargeType("charge"); setBankReference(""); setShowManualCharge(false);
      if (data) await load();
    } catch (err) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not save entry", variant: "destructive" });
    } finally {
      setCharging(false);
    }
  }

  return (
    <AdminLayout current="/admin/cash-fees">
      <div className="max-w-4xl mx-auto p-3 sm:p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Cash Fee Ledger</h1>
          <p className="text-sm text-gray-500 mt-1">
            Shop balances owed to Ayrshire Hub. Record bank payments here; unpaid balances are collected through the normal card-payment process.
          </p>
        </div>

        {/* Manual Charge */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() => setShowManualCharge((v) => !v)}
            className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
          >
            <Plus size={18} className="text-[#1e3a5f] shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-foreground">Add charge, credit or bank payment</div>
              <div className="text-sm text-gray-500">Record money received by bank transfer, add a fee or grant goodwill credit</div>
            </div>
            {showManualCharge ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
          </button>
          {showManualCharge && (
            <form onSubmit={(e) => void submitManualCharge(e)} className="border-t border-gray-100 px-5 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <div className="flex flex-wrap gap-2">
                  {(["charge", "credit", "bank_payment"] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setChargeType(t)}
                      disabled={charging}
                      className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-all ${chargeType === t
                        ? t === "charge" ? "bg-red-600 text-white border-red-600" : "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      {t === "charge" ? "Charge (adds to owed)" : t === "credit" ? "Goodwill credit" : "Bank transfer payment received"}
                    </button>
                  ))}
                </div>
              </div>
              {chargeType === "bank_payment" && <p className="text-sm text-emerald-800">Record a payment that has arrived in your bank. This reduces the shop’s debt and appears in both ledgers. It does not reduce your income.</p>}
              <div>
                <label htmlFor="ledger-shop" className="block text-sm font-medium text-gray-700 mb-1">Shop</label>
                <select
                  id="ledger-shop" disabled={charging}
                  value={chargeStoreId}
                  onChange={(e) => setChargeStoreId(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
                >
                  <option value="">Select a shop…</option>
                  {stores.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {stores.length === 0 && <p className="text-xs text-gray-400 mt-1">Loading shops…</p>}
              </div>
              <div>
                <label htmlFor="ledger-amount" className="block text-sm font-medium text-gray-700 mb-1">Amount (£)</label>
                <input
                  id="ledger-amount" disabled={charging}
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={chargeAmount}
                  onChange={(e) => setChargeAmount(e.target.value)}
                  placeholder="e.g. 44.05"
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
                />
              </div>
              {chargeType === "bank_payment" && <>
                <div><label htmlFor="bank-payment-date" className="block text-sm font-medium text-gray-700 mb-1">Payment date</label><input id="bank-payment-date" type="date" value={paymentDate} max={new Intl.DateTimeFormat("en-CA", {timeZone: "Europe/London"}).format(new Date())} onChange={e => setPaymentDate(e.target.value)} required disabled={charging} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label htmlFor="bank-payment-reference" className="block text-sm font-medium text-gray-700 mb-1">Bank reference</label><input id="bank-payment-reference" value={bankReference} onChange={e => setBankReference(e.target.value)} maxLength={100} required disabled={charging} placeholder="Reference shown on your bank statement" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
              </>}
              <div>
                <label htmlFor="ledger-note" className="block text-sm font-medium text-gray-700 mb-1">Note <span className="text-gray-400 font-normal">(shown on ledger)</span></label>
                <textarea
                  id="ledger-note" maxLength={500} disabled={charging}
                  value={chargeNote}
                  onChange={(e) => setChargeNote(e.target.value)}
                  placeholder={chargeType === "bank_payment" ? "Optional payment note" : "e.g. Payment dispute fee — Mastercard chargeback 08/2026"}
                  required={chargeType !== "bank_payment"}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={charging}
                  className="px-4 py-2 bg-[#1e3a5f] text-white text-sm font-semibold rounded-lg hover:bg-[#162d4a] disabled:opacity-50"
                >
                  {charging ? "Saving…" : chargeType === "bank_payment" ? "Record bank payment" : chargeType === "credit" ? "Add credit" : "Add charge"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowManualCharge(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {!data && !loading && (
          <button
            onClick={() => void load()}
            className="px-4 py-2 bg-[#1e3a5f] text-white rounded-lg text-sm font-semibold hover:bg-[#162d4a]"
          >
            Load Ledger
          </button>
        )}

        {loading && (
          <div className="text-center py-12 text-gray-400 text-sm">Loading…</div>
        )}

        {data && !loading && (
          <>
            {/* Grand total banner */}
            <div className={`rounded-xl p-5 flex flex-wrap items-center gap-4 ${data.grandTotalOwedPence > 0 ? "bg-amber-50 dark:bg-amber-900/20 border border-amber-200" : "bg-green-50 border border-green-200"}`}>
              {data.grandTotalOwedPence > 0 ? (
                <AlertCircle className="text-amber-500 shrink-0" size={28} />
              ) : (
                <CheckCircle className="text-green-500 shrink-0" size={28} />
              )}
              <div>
                <div className={`text-2xl font-bold ${data.grandTotalOwedPence > 0 ? "text-amber-700" : "text-green-700"}`}>
                  {pence(data.grandTotalOwedPence)} outstanding
                </div>
                <div className="text-sm text-gray-500 mt-0.5">
                  {data.grandTotalOwedPence > 0
                    ? `Across ${data.stores.length} shop${data.stores.length !== 1 ? "s" : ""}. Pending payments are shown separately; they are not money received.`
                    : "No shop balance is outstanding"}
                </div>
              </div>
              <button
                onClick={() => void load()}
                className="ml-auto px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
              >
                Refresh
              </button>
            </div>

            {data.stores.length === 0 && (
              <div className="text-center py-12 text-gray-400 text-sm">No outstanding shop balances.</div>
            )}

            {/* Per-store cards */}
            {data.stores.map((store) => {
              const isOpen = expanded.has(store.storeId);
              return (
                <div key={store.storeId} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  {/* Store header row */}
                  <div className="flex flex-wrap items-center gap-3 px-5 py-4">
                    <button
                      onClick={() => toggleExpand(store.storeId)}
                      aria-label={`View charges for ${store.storeName}`}
                      aria-expanded={isOpen}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-foreground">{store.storeName}</div>
                      <div className="text-sm text-gray-500">
                        {pence(store.chargedPence)} charged · {pence(store.confirmedPaymentsPence ?? store.creditedPence)} payments / proceeds received
                        {(store.otherCreditsPence ?? 0)>0 && <> · {pence(store.otherCreditsPence!)} other confirmed credits</>}
                        {" · "}owed: <span className="font-semibold text-amber-700">{pence(store.outstandingPence)}</span>
                      </div>
                      {(store.pendingPence ?? 0)>0 && <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-sm text-amber-950">{pence(store.pendingPence!)} payment pending — not received. The full {pence(store.outstandingPence)} remains owed until payment is confirmed. Additional amount available to pay: {pence(store.payablePence ?? Math.max(0,store.outstandingPence-store.pendingPence!))}.</p>}
                      <a className="mt-1 inline-flex min-h-10 items-center text-sm font-semibold text-blue-700 underline" href={`/business/finance/${store.storeId}`}>View ledger</a>
                    </div>
                    <button type="button" onClick={() => { setChargeStoreId(String(store.storeId)); setChargeType("bank_payment"); setChargeAmount((store.outstandingPence / 100).toFixed(2)); setShowManualCharge(true); window.scrollTo({top: 0, behavior: "smooth"}); }} className="px-3 py-1.5 bg-[#1e3a5f] text-white text-sm font-semibold rounded-lg">Record bank payment</button>
                    <button
                      onClick={() => void settleStore(store)}
                      disabled={settling === store.storeId}
                      className="px-3 py-1.5 bg-[#059669] text-white text-sm font-semibold rounded-lg hover:bg-[#047857] disabled:opacity-50"
                    >
                      {settling === store.storeId ? "Writing off…" : `Write off ${pence(store.outstandingPence)}`}
                    </button>
                  </div>

                  {/* Charge rows — each a posted debit against the shop */}
                  {isOpen && (
                    <div className="border-t border-gray-100">
                      <div className="px-5 py-2 bg-gray-50 grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_auto_auto] gap-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        <span>Charge</span>
                        <span className="hidden sm:block text-right">Order total</span>
                        <span className="text-right">Amount</span>
                      </div>
                      {store.charges.map((charge,index) => (
                        <div key={`${charge.transactionId}:${index}`} className="px-5 py-3 grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_auto_auto] gap-3 items-center border-t border-gray-50 text-sm">
                          <div className="min-w-0 break-words">
                            <span className="font-medium text-gray-800">{charge.label}</span>
                            {charge.orderId ? (
                              <>
                                <span className="text-gray-400 mx-1.5">·</span>
                                <span className="text-gray-600">#{charge.orderId}{charge.destinationPostcode ? ` · ${charge.destinationPostcode}` : ""}</span>
                              </>
                            ) : null}
                            {charge.customerName ? (
                              <>
                                <span className="text-gray-400 mx-1.5">·</span>
                                <span className="text-gray-600">{charge.customerName}</span>
                              </>
                            ) : null}
                            {charge.fulfilmentType ? (
                              <>
                                <span className="text-gray-400 mx-1.5">·</span>
                                <span className="text-gray-400 capitalize">{charge.fulfilmentType}</span>
                              </>
                            ) : null}
                            <div className="text-xs text-gray-400 mt-0.5">{formatDate(charge.createdAt)}</div><div className="sm:hidden text-xs text-gray-500">Order total: {charge.totalOrderPence != null ? pence(charge.totalOrderPence) : "—"}</div>
                          </div>
                          <div className="hidden sm:block text-right text-gray-600">
                            {charge.totalOrderPence != null ? pence(charge.totalOrderPence) : "—"}
                          </div>
                          <div className="text-right font-semibold text-amber-700">{pence(charge.amountPence)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
